import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Users, Package, Banknote, DollarSign, TrendingUp, TrendingDown, 
  Receipt, ShoppingCart, AlertTriangle, CheckCircle, Clock, CreditCard,
  ArrowUpRight, ArrowDownRight, Calendar
} from "lucide-react";
import { Customer } from "@/types/customer";
import { Product } from "@/types/product";
import { Expense } from "@/types/expense";

interface DashboardPageProps {
  customers: Customer[];
  products: Product[];
  expenses: Expense[];
  onNavigate: (page: "dashboard" | "customers" | "sales" | "payments" | "expenses" | "notifications" | "reports" | "users" | "settings") => void;
}

export function DashboardPage({ customers, products, expenses, onNavigate }: DashboardPageProps) {
  // Calculate statistics
  const stats = useMemo(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const today = `${year}-${month}-${day}`;
    const thisMonth = today.slice(0, 7);
    
    // Products stats
    const activeProducts = products.filter(p => p.remainingAmount > 0);
    const completedProducts = products.filter(p => p.remainingAmount <= 0.001);
    const todaySales = products.filter(p => p.date === today);
    const monthSales = products.filter(p => p.date?.startsWith(thisMonth));
    
    // Financial stats - IQD
    const totalSalesIQD = products.filter(p => p.currency === "IQD").reduce((sum, p) => sum + p.salePrice, 0);
    const totalPaidIQD = products.filter(p => p.currency === "IQD").reduce((sum, p) => sum + p.paidAmount, 0);
    const totalRemainingIQD = products.filter(p => p.currency === "IQD").reduce((sum, p) => sum + p.remainingAmount, 0);
    
    // Financial stats - USD
    const totalSalesUSD = products.filter(p => p.currency === "USD").reduce((sum, p) => sum + p.salePrice, 0);
    const totalPaidUSD = products.filter(p => p.currency === "USD").reduce((sum, p) => sum + p.paidAmount, 0);
    const totalRemainingUSD = products.filter(p => p.currency === "USD").reduce((sum, p) => sum + p.remainingAmount, 0);
    
    // Today's sales
    const todaySalesIQD = todaySales.filter(p => p.currency === "IQD").reduce((sum, p) => sum + p.salePrice, 0);
    const todaySalesUSD = todaySales.filter(p => p.currency === "USD").reduce((sum, p) => sum + p.salePrice, 0);
    
    // This month sales
    const monthSalesIQD = monthSales.filter(p => p.currency === "IQD").reduce((sum, p) => sum + p.salePrice, 0);
    const monthSalesUSD = monthSales.filter(p => p.currency === "USD").reduce((sum, p) => sum + p.salePrice, 0);

    // Expenses stats
    const todayExpenses = expenses.filter(e => e.date === today);
    const monthExpenses = expenses.filter(e => e.date?.startsWith(thisMonth));
    const todayExpensesIQD = todayExpenses.filter(e => e.currency === "IQD").reduce((sum, e) => sum + e.amount, 0);
    const todayExpensesUSD = todayExpenses.filter(e => e.currency === "USD").reduce((sum, e) => sum + e.amount, 0);
    const monthExpensesIQD = monthExpenses.filter(e => e.currency === "IQD").reduce((sum, e) => sum + e.amount, 0);
    const monthExpensesUSD = monthExpenses.filter(e => e.currency === "USD").reduce((sum, e) => sum + e.amount, 0);
    const totalExpensesIQD = expenses.filter(e => e.currency === "IQD").reduce((sum, e) => sum + e.amount, 0);
    const totalExpensesUSD = expenses.filter(e => e.currency === "USD").reduce((sum, e) => sum + e.amount, 0);
    
    // Overdue customers (customers with remaining > 0)
    const customersWithDebt = customers.filter(c => 
      products.filter(p => p.customerId === c.id && p.remainingAmount > 0).length > 0
    );
    
    // Recent sales (last 5)
    const recentSales = [...products].sort((a, b) => 
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    ).slice(0, 5);
    
    // Recent expenses (last 5)
    const recentExpenses = [...expenses].sort((a, b) => 
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    ).slice(0, 5);
    
    return {
      customers: { total: customers.length, withDebt: customersWithDebt.length, blocked: customers.filter(c => c.isBlocked).length },
      products: { total: products.length, active: activeProducts.length, completed: completedProducts.length, todayCount: todaySales.length, monthCount: monthSales.length },
      sales: { totalIQD: totalSalesIQD, totalUSD: totalSalesUSD, todayIQD: todaySalesIQD, todayUSD: todaySalesUSD, monthIQD: monthSalesIQD, monthUSD: monthSalesUSD },
      paid: { totalIQD: totalPaidIQD, totalUSD: totalPaidUSD },
      remaining: { totalIQD: totalRemainingIQD, totalUSD: totalRemainingUSD },
      expenses: { totalIQD: totalExpensesIQD, totalUSD: totalExpensesUSD, todayIQD: todayExpensesIQD, todayUSD: todayExpensesUSD, monthIQD: monthExpensesIQD, monthUSD: monthExpensesUSD, todayCount: todayExpenses.length, monthCount: monthExpenses.length },
      recentSales,
      recentExpenses,
    };
  }, [customers, products, expenses]);

  const getCustomerName = (customerId: string) => customers.find(c => c.id === customerId)?.fullName || "نەزانراو";

  return (
    <div className="space-y-4" dir="rtl">
      {/* Quick Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="rounded-xl border bg-card p-4 cursor-pointer hover:border-primary/50 transition-colors" onClick={() => onNavigate("customers")}>
          <div className="flex items-center justify-between mb-2">
            <div className="h-8 w-8 rounded-lg bg-blue-500/10 flex items-center justify-center">
              <Users className="h-4 w-4 text-blue-600" />
            </div>
            <ArrowUpRight className="h-4 w-4 text-muted-foreground" />
          </div>
          <p className="text-2xl font-bold">{stats.customers.total}</p>
          <p className="text-xs text-muted-foreground">کۆی کڕیارەکان</p>
        </div>
        
        <div className="rounded-xl border bg-card p-4 cursor-pointer hover:border-primary/50 transition-colors" onClick={() => onNavigate("sales")}>
          <div className="flex items-center justify-between mb-2">
            <div className="h-8 w-8 rounded-lg bg-green-500/10 flex items-center justify-center">
              <ShoppingCart className="h-4 w-4 text-green-600" />
            </div>
            <ArrowUpRight className="h-4 w-4 text-muted-foreground" />
          </div>
          <p className="text-2xl font-bold">{stats.products.total}</p>
          <p className="text-xs text-muted-foreground">کۆی فرۆشتنەکان</p>
        </div>
        
        <div className="rounded-xl border bg-card p-4 cursor-pointer hover:border-primary/50 transition-colors" onClick={() => onNavigate("notifications")}>
          <div className="flex items-center justify-between mb-2">
            <div className="h-8 w-8 rounded-lg bg-amber-500/10 flex items-center justify-center">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
            </div>
            <ArrowUpRight className="h-4 w-4 text-muted-foreground" />
          </div>
          <p className="text-2xl font-bold text-amber-600">{stats.customers.withDebt}</p>
          <p className="text-xs text-muted-foreground">کڕیار قەرزدار</p>
        </div>
        
        <div className="rounded-xl border bg-card p-4 cursor-pointer hover:border-primary/50 transition-colors" onClick={() => onNavigate("expenses")}>
          <div className="flex items-center justify-between mb-2">
            <div className="h-8 w-8 rounded-lg bg-red-500/10 flex items-center justify-center">
              <Receipt className="h-4 w-4 text-red-600" />
            </div>
            <ArrowUpRight className="h-4 w-4 text-muted-foreground" />
          </div>
          <p className="text-2xl font-bold">{expenses.length}</p>
          <p className="text-xs text-muted-foreground">کۆی خەرجییەکان</p>
        </div>
      </div>

      {/* Today's Summary */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <Calendar className="h-5 w-5" />
            ئەمڕۆ
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <ShoppingCart className="h-3 w-3" /> فرۆشتن
              </p>
              <p className="text-lg font-bold">{stats.products.todayCount}</p>
              <div className="text-xs text-muted-foreground">
                {stats.sales.todayIQD > 0 && <span className="block"><span dir="ltr">{stats.sales.todayIQD.toLocaleString()}</span> د.ع</span>}
                {stats.sales.todayUSD > 0 && <span className="block" dir="ltr">${stats.sales.todayUSD.toLocaleString()}</span>}
                {stats.sales.todayIQD === 0 && stats.sales.todayUSD === 0 && <span>-</span>}
              </div>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <Receipt className="h-3 w-3" /> خەرجی
              </p>
              <p className="text-lg font-bold text-red-600">{stats.expenses.todayCount}</p>
              <div className="text-xs text-muted-foreground">
                {stats.expenses.todayIQD > 0 && <span className="block"><span dir="ltr">{stats.expenses.todayIQD.toLocaleString()}</span> د.ع</span>}
                {stats.expenses.todayUSD > 0 && <span className="block" dir="ltr">${stats.expenses.todayUSD.toLocaleString()}</span>}
                {stats.expenses.todayIQD === 0 && stats.expenses.todayUSD === 0 && <span>-</span>}
              </div>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <TrendingUp className="h-3 w-3 text-green-600" /> صافی ئەمڕۆ (دینار)
              </p>
              <p className={`text-lg font-bold ${stats.sales.todayIQD - stats.expenses.todayIQD >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                <span dir="ltr">{(stats.sales.todayIQD - stats.expenses.todayIQD).toLocaleString()}</span>
              </p>
              <p className="text-xs text-muted-foreground">د.ع</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <TrendingUp className="h-3 w-3 text-green-600" /> صافی ئەمڕۆ (دۆلار)
              </p>
              <p className={`text-lg font-bold ${stats.sales.todayUSD - stats.expenses.todayUSD >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                <span dir="ltr">${(stats.sales.todayUSD - stats.expenses.todayUSD).toLocaleString()}</span>
              </p>
              <p className="text-xs text-muted-foreground">دۆلار</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Financial Overview */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* IQD Summary */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <Banknote className="h-5 w-5 text-amber-600" />
              دینار (د.ع)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between items-center py-2 border-b">
              <span className="text-sm text-muted-foreground">کۆی فرۆشتن</span>
              <span className="font-bold flex items-center gap-1">
                <span dir="ltr">{stats.sales.totalIQD.toLocaleString()}</span>
                <span className="text-xs font-normal text-muted-foreground">د.ع</span>
              </span>
            </div>
            <div className="flex justify-between items-center py-2 border-b">
              <span className="text-sm text-muted-foreground">وەرگیراو</span>
              <span className="font-bold text-green-600 flex items-center gap-1">
                <span dir="ltr">{stats.paid.totalIQD.toLocaleString()}</span>
                <span className="text-xs font-normal text-muted-foreground">د.ع</span>
              </span>
            </div>
            <div className="flex justify-between items-center py-2 border-b">
              <span className="text-sm text-muted-foreground">ماوە (قەرز)</span>
              <span className="font-bold text-red-600 flex items-center gap-1">
                <span dir="ltr">{stats.remaining.totalIQD.toLocaleString()}</span>
                <span className="text-xs font-normal text-muted-foreground">د.ع</span>
              </span>
            </div>
            <div className="flex justify-between items-center py-2 border-b">
              <span className="text-sm text-muted-foreground">کۆی خەرجی</span>
              <span className="font-bold text-orange-600 flex items-center gap-1">
                <span dir="ltr">{stats.expenses.totalIQD.toLocaleString()}</span>
                <span className="text-xs font-normal text-muted-foreground">د.ع</span>
              </span>
            </div>
            <div className="flex justify-between items-center py-2 bg-muted/50 rounded-lg px-2">
              <span className="text-sm font-medium">قازانجی خاوەن (وەرگیراو - خەرجی)</span>
              <span className={`font-bold flex items-center gap-1 ${stats.paid.totalIQD - stats.expenses.totalIQD >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                <span dir="ltr">{(stats.paid.totalIQD - stats.expenses.totalIQD).toLocaleString()}</span>
                <span className="text-xs font-normal text-muted-foreground">د.ع</span>
              </span>
            </div>
          </CardContent>
        </Card>

        {/* USD Summary */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <DollarSign className="h-5 w-5 text-green-600" />
              دۆلار ($)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between items-center py-2 border-b">
              <span className="text-sm text-muted-foreground">کۆی فرۆشتن</span>
              <span className="font-bold" dir="ltr">${stats.sales.totalUSD.toLocaleString()}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b">
              <span className="text-sm text-muted-foreground">وەرگیراو</span>
              <span className="font-bold text-green-600" dir="ltr">${stats.paid.totalUSD.toLocaleString()}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b">
              <span className="text-sm text-muted-foreground">ماوە (قەرز)</span>
              <span className="font-bold text-red-600" dir="ltr">${stats.remaining.totalUSD.toLocaleString()}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b">
              <span className="text-sm text-muted-foreground">کۆی خەرجی</span>
              <span className="font-bold text-orange-600" dir="ltr">${stats.expenses.totalUSD.toLocaleString()}</span>
            </div>
            <div className="flex justify-between items-center py-2 bg-muted/50 rounded-lg px-2">
              <span className="text-sm font-medium">قازانجی خاوەن (وەرگیراو - خەرجی)</span>
              <span className={`font-bold ${stats.paid.totalUSD - stats.expenses.totalUSD >= 0 ? 'text-green-600' : 'text-red-600'}`} dir="ltr">
                ${(stats.paid.totalUSD - stats.expenses.totalUSD).toLocaleString()}
              </span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* This Month Summary */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <Clock className="h-5 w-5" />
            ئەم مانگە
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="rounded-lg bg-green-500/10 p-3">
              <p className="text-xs text-muted-foreground mb-1">فرۆشتن</p>
              <p className="text-lg font-bold">{stats.products.monthCount}</p>
              <div className="text-xs mt-1">
                {stats.sales.monthIQD > 0 && <p><span dir="ltr">{stats.sales.monthIQD.toLocaleString()}</span> د.ع</p>}
                {stats.sales.monthUSD > 0 && <p dir="ltr">${stats.sales.monthUSD.toLocaleString()}</p>}
              </div>
            </div>
            <div className="rounded-lg bg-red-500/10 p-3">
              <p className="text-xs text-muted-foreground mb-1">خەرجی</p>
              <p className="text-lg font-bold">{stats.expenses.monthCount}</p>
              <div className="text-xs mt-1">
                {stats.expenses.monthIQD > 0 && <p><span dir="ltr">{stats.expenses.monthIQD.toLocaleString()}</span> د.ع</p>}
                {stats.expenses.monthUSD > 0 && <p dir="ltr">${stats.expenses.monthUSD.toLocaleString()}</p>}
              </div>
            </div>
            <div className="rounded-lg bg-blue-500/10 p-3">
              <p className="text-xs text-muted-foreground mb-1">قازانج (دینار)</p>
              <p className={`text-lg font-bold ${stats.sales.monthIQD - stats.expenses.monthIQD >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                <span dir="ltr">{(stats.sales.monthIQD - stats.expenses.monthIQD).toLocaleString()}</span>
              </p>
              <p className="text-xs text-muted-foreground">د.ع</p>
            </div>
            <div className="rounded-lg bg-emerald-500/10 p-3">
              <p className="text-xs text-muted-foreground mb-1">قازانج (دۆلار)</p>
              <p className={`text-lg font-bold ${stats.sales.monthUSD - stats.expenses.monthUSD >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                <span dir="ltr">${(stats.sales.monthUSD - stats.expenses.monthUSD).toLocaleString()}</span>
              </p>
              <p className="text-xs text-muted-foreground">دۆلار</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Recent Activity */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* Recent Sales */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center justify-between text-base">
              <span className="flex items-center gap-2">
                <ShoppingCart className="h-5 w-5 text-green-600" />
                دوایین فرۆشتنەکان
              </span>
              <Badge variant="secondary" className="cursor-pointer" onClick={() => onNavigate("sales")}>
                هەموو
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats.recentSales.length === 0 ? (
              <p className="text-center text-muted-foreground py-4">هیچ فرۆشتنێک نییە</p>
            ) : (
              <div className="space-y-2">
                {stats.recentSales.map((sale) => (
                  <div key={sale.id} className="flex items-center justify-between py-2 border-b last:border-0">
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-full bg-green-500/10 flex items-center justify-center">
                        <Package className="h-4 w-4 text-green-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium truncate max-w-[120px]">{sale.deviceName}</p>
                        <p className="text-xs text-muted-foreground">{getCustomerName(sale.customerId)}</p>
                      </div>
                    </div>
                    <div className="text-left">
                      <p className="text-sm font-bold flex items-center justify-end gap-1">
                        {sale.currency === "IQD" ? (
                          <>
                            <span dir="ltr">{sale.salePrice.toLocaleString()}</span>
                            <span className="text-xs font-normal text-muted-foreground">د.ع</span>
                          </>
                        ) : (
                          <span dir="ltr">${sale.salePrice.toLocaleString()}</span>
                        )}
                      </p>
                      <p className="text-xs text-muted-foreground" dir="ltr">{sale.date}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Expenses */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center justify-between text-base">
              <span className="flex items-center gap-2">
                <Receipt className="h-5 w-5 text-red-600" />
                دوایین خەرجییەکان
              </span>
              <Badge variant="secondary" className="cursor-pointer" onClick={() => onNavigate("expenses")}>
                هەموو
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats.recentExpenses.length === 0 ? (
              <p className="text-center text-muted-foreground py-4">هیچ خەرجییەک نییە</p>
            ) : (
              <div className="space-y-2">
                {stats.recentExpenses.map((expense) => (
                  <div key={expense.id} className="flex items-center justify-between py-2 border-b last:border-0">
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-full bg-red-500/10 flex items-center justify-center">
                        <Receipt className="h-4 w-4 text-red-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium truncate max-w-[120px]">{expense.title}</p>
                        <p className="text-xs text-muted-foreground">{expense.category}</p>
                      </div>
                    </div>
                    <div className="text-left">
                      <p className="text-sm font-bold text-red-600 flex items-center justify-end gap-1">
                        {expense.currency === "IQD" ? (
                          <>
                            <span dir="ltr">{expense.amount.toLocaleString()}</span>
                            <span className="text-xs font-normal text-muted-foreground">د.ع</span>
                          </>
                        ) : (
                          <span dir="ltr">${expense.amount.toLocaleString()}</span>
                        )}
                      </p>
                      <p className="text-xs text-muted-foreground" dir="ltr">{expense.date}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Status Overview */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <CreditCard className="h-5 w-5" />
            دۆخی قیستەکان
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center p-3 rounded-lg bg-amber-500/10">
              <Clock className="h-6 w-6 text-amber-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-amber-600">{stats.products.active}</p>
              <p className="text-xs text-muted-foreground">چالاک</p>
            </div>
            <div className="text-center p-3 rounded-lg bg-green-500/10">
              <CheckCircle className="h-6 w-6 text-green-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-green-600">{stats.products.completed}</p>
              <p className="text-xs text-muted-foreground">تەواوبوو</p>
            </div>
            <div className="text-center p-3 rounded-lg bg-blue-500/10">
              <Package className="h-6 w-6 text-blue-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-blue-600">{stats.products.total}</p>
              <p className="text-xs text-muted-foreground">کۆی گشتی</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
